/**
 * Main-thread side of search: feeds the worker the whole graph in idle-time chunks after the first
 * paint, then every change (debounced), and answers queries (only the latest one counts). Until
 * the worker is ready, the command bar shows the title tier alone.
 */
import type { Graph, Node, NodeId } from '@solar/core'
import type { Hit, SlimNode } from './search-index.ts'
import type { FromWorker, ToWorker } from './search-worker.ts'

const CHUNK = 4000

export class SearchClient {
  private readonly g: Graph
  private worker: Worker | null = null
  private dirty = new Set<NodeId>()
  private timer: ReturnType<typeof setTimeout> | null = null
  private req = 0
  private readonly waiting = new Map<number, (hits: Hit[]) => void>()
  ready = false
  readyMs = 0
  lastMs = 0
  /** Incremented when the initial index is complete (views re-run their query). */
  version = 0
  private readonly listeners = new Set<() => void>()

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getVersion = (): number => this.version

  constructor(g: Graph) {
    this.g = g
  }

  private post(m: ToWorker): void {
    this.worker?.postMessage(m)
  }

  private slim(n: Node): SlimNode {
    let fields = ''
    for (const [k, v] of Object.entries(n.props)) {
      if (k.startsWith('sys:')) continue
      for (const x of Array.isArray(v) ? v : [v]) {
        if (typeof x === 'string') fields += ` ${this.g.get(x)?.title ?? x}`
        else if (typeof x === 'number' || typeof x === 'boolean') fields += ` ${x}`
      }
    }
    return { id: n.id, content: n.kind === 'block' ? n.content : '', fields: fields.trim(), updatedAt: n.updatedAt }
  }

  /** Start the worker and feed it the graph in idle time. */
  start(): void {
    if (this.worker || typeof Worker === 'undefined') return
    this.worker = new Worker(new URL('./search-worker.ts', import.meta.url), { type: 'module' })
    this.worker.onmessage = (e: MessageEvent<FromWorker>) => {
      const m = e.data
      if (m.type === 'ready') {
        this.ready = true
        this.readyMs = m.ms
        this.version++
        this.listeners.forEach((f) => f())
      } else {
        this.lastMs = m.ms
        this.waiting.get(m.req)?.(m.hits)
        this.waiting.delete(m.req)
      }
    }
    this.g.subscribe((cs) => {
      for (const id of cs.nodes) this.dirty.add(id)
      if (this.timer === null) this.timer = setTimeout(() => this.flush(), 300)
    })
    const ids = [...this.g.nodes()].filter((n) => n.deletedAt === null && n.kind === 'block' && n.content !== '').map((n) => n.id)
    let i = 0
    const step = (): void => {
      const batch: SlimNode[] = []
      for (const end = Math.min(i + CHUNK, ids.length); i < end; i++) {
        const n = this.g.get(ids[i]!)
        if (n && n.deletedAt === null) batch.push(this.slim(n))
      }
      this.post({ type: 'upsert', nodes: batch, done: i >= ids.length })
      if (i < ids.length) idle(step)
    }
    idle(step)
  }

  private flush(): void {
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    const up: SlimNode[] = []
    const gone: string[] = []
    for (const id of this.dirty) {
      const n = this.g.get(id)
      if (n && n.deletedAt === null && n.kind === 'block') up.push(this.slim(n))
      else gone.push(id)
    }
    this.dirty.clear()
    if (up.length) this.post({ type: 'upsert', nodes: up })
    if (gone.length) this.post({ type: 'remove', ids: gone })
  }

  /** Blocks matching the query, best first; a newer query makes older ones resolve empty. */
  search(q: string, limit = 30): Promise<Hit[]> {
    if (!this.worker || q.trim() === '') return Promise.resolve([])
    // Changes not yet sent go first: the worker handles messages in order, so the query sees them.
    if (this.dirty.size > 0) this.flush()
    const req = ++this.req
    for (const [r, resolve] of this.waiting) {
      resolve([])
      this.waiting.delete(r)
    }
    return new Promise((resolve) => {
      this.waiting.set(req, resolve)
      this.post({ type: 'search', req, q, limit })
    })
  }
}

function idle(fn: () => void): void {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => fn(), { timeout: 1000 })
  else setTimeout(fn, 16)
}
